import React from 'react';
import { Box, Container, Typography, Grid, Link as MuiLink, Divider, Stack, Chip, IconButton } from '@mui/material';
import { Bolt, Email, Phone, GitHub, LinkedIn, Twitter } from '@mui/icons-material';

const Footer = () => {
  return (
    <Box sx={{
      position: 'relative', overflow: 'hidden',
      bgcolor: '#0c0a1d', color: 'white', mt: 'auto', pt: 10, pb: 4,
      '&::before': {
        content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(79,70,229,0.3), transparent)',
      },
    }}>
      {/* Subtle glow */}
      <Box sx={{ position: 'absolute', top: -200, left: '50%', transform: 'translateX(-50%)', width: 600, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(79,70,229,0.04) 0%, transparent 60%)', filter: 'blur(40px)' }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={6} sx={{ mb: 8 }}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 3 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, #4f46e5, #06b6d4)',
              }}>
                <Bolt sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>SpareConnect AI</Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', lineHeight: 1.9, mb: 3, maxWidth: 280 }}>
              India's AI-powered B2B marketplace for industrial surplus.
              Recover value from idle inventory with intelligent matching, pricing, and logistics.
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <Chip label="AI Powered" size="small" sx={{ bgcolor: 'rgba(79,70,229,0.12)', color: '#a5b4fc', fontSize: '0.68rem', fontWeight: 600 }} />
              <Chip label="GST Verified" size="small" sx={{ bgcolor: 'rgba(16,185,129,0.12)', color: '#6ee7b7', fontSize: '0.68rem', fontWeight: 600 }} />
              <Chip label="Razorpay Secured" size="small" sx={{ bgcolor: 'rgba(6,182,212,0.12)', color: '#67e8f9', fontSize: '0.68rem', fontWeight: 600 }} />
            </Stack>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.12em', display: 'block', mb: 2.5 }}>
              Platform
            </Typography>
            <Stack spacing={1.8}>
              {['Marketplace', 'AI Search', 'Upload Inventory', 'Bulk Upload', 'Analytics'].map((item) => (
                <MuiLink key={item} href="#" underline="none" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.84rem', fontWeight: 400, '&:hover': { color: '#a5b4fc' }, transition: 'color 0.2s' }}>
                  {item}
                </MuiLink>
              ))}
            </Stack>
          </Grid>
          <Grid item xs={6} md={2}>
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.12em', display: 'block', mb: 2.5 }}>
              Company
            </Typography>
            <Stack spacing={1.8}>
              {['About Us', 'Careers', 'Blog', 'Contact', 'Partners'].map((item) => (
                <MuiLink key={item} href="#" underline="none" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.84rem', fontWeight: 400, '&:hover': { color: '#a5b4fc' }, transition: 'color 0.2s' }}>
                  {item}
                </MuiLink>
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.12em', display: 'block', mb: 2.5 }}>
              Industries Served
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
              {['Manufacturing', 'Automotive', 'Pharma', 'Oil & Gas', 'Power', 'Food Processing', 'Textiles', 'Construction'].map((ind) => (
                <Chip key={ind} label={ind} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', borderRadius: 2, border: '1px solid rgba(255,255,255,0.06)' }} />
              ))}
            </Box>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Email sx={{ fontSize: 15, color: 'rgba(255,255,255,0.3)' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>hello@spareconnect.ai</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Phone sx={{ fontSize: 15, color: 'rgba(255,255,255,0.3)' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>+91 98765 43210</Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mb: 4 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)' }}>
            © 2024 SpareConnect AI. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <MuiLink href="#" underline="none" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.7rem', '&:hover': { color: '#a5b4fc' } }}>Privacy</MuiLink>
            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)' }} />
            <MuiLink href="#" underline="none" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.7rem', '&:hover': { color: '#a5b4fc' } }}>Terms</MuiLink>
            <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)' }} />
            <MuiLink href="#" underline="none" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.7rem', '&:hover': { color: '#a5b4fc' } }}>Refund Policy</MuiLink>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;

